import type { DeviceRecord, DirectEpochTransitionClaim, IdentityId, PublicKey } from '@shared/types';
import { directMessagesApi } from '../api/directMessages';
import { getDeviceForIdentityId } from '../services/activeDevice';
import { bytesToBase64, base64ToBytes } from '../utils/encoding';
import {
  computeDirectKeyCommitment,
  decryptDirectKeyEnvelope,
  encryptDirectKeyEnvelope,
  generateDirectKey
} from './crypto';
import { getDirectChatId, getDirectChatParticipants } from './ids';
import { deleteDirectChatKey, getDirectChatKey, saveDirectChatKey } from './storage';
import { signDirectEpochTransition, verifyDirectEpochTransition } from '../groupChats/epochTransition';
import type { SigningKey } from '../crypto/ed25519Provider';
import { useVaultStore } from '../store/vaultStore';
import { isDirectGuestContact } from '../utils/directGuest';
import { logChatCrypto, toCommitmentHint } from '../utils/chatCryptoDebug';
import { publishTempDeviceDirectEnvelopes } from '../temporaryAccess/tempDeviceRekey';

export type DirectEpochContext = {
  localIdentityId: IdentityId;
  localServerUrl?: string;
  localPublicKey: PublicKey;
  localEcdhKey: CryptoKey | null;
  localEcdhScalar: Uint8Array;
  localSigningKey: SigningKey;
  peerIdentityId: IdentityId;
  peerPublicKey: PublicKey;
};

type DirectEpochResolution = { key: Uint8Array; epoch: number };

type DirectEpochFetchResult = {
  directChatId: string;
  participantIdentityIds: [string, string];
  epoch: number;
  keyCommitment: string | null;
  signedEpochTransition?: DirectEpochTransitionClaim | null;
  proposerIdentityId?: string | null;
  proposerDeviceId?: string | null;
  envelopeCiphertext: string | null;
  publisherIdentityId: string | null;
  identityPublicKeys: Record<string, PublicKey>;
  devicesByIdentityId: Record<string, DeviceRecord[]>;
  hasEnvelope: boolean;
  hasEpochKey?: boolean;
  canRecoverByRekey?: boolean;
};

const latestDirectEpochRecoveryInFlight = new Map<string, Promise<DirectEpochResolution | null>>();

async function fetchDirectEpochState(
  params: DirectEpochContext,
  epoch?: number,
  onError?: (error: unknown) => void
): Promise<DirectEpochFetchResult | null> {
  let response: Awaited<ReturnType<typeof directMessagesApi.fetchEpochKey>> | null;
  try {
    response = await directMessagesApi.fetchEpochKey(
      epoch === undefined ? { peerIdentityId: params.peerIdentityId } : { peerIdentityId: params.peerIdentityId, epoch },
      params.localServerUrl,
      params.localIdentityId
    );
  } catch (error) {
    onError?.(error);
    logChatCrypto('direct', 'fetch-failed', {
      directChatId: getDirectChatId(params.localIdentityId, params.peerIdentityId),
      requestedEpoch: epoch ?? 'latest',
      error: error instanceof Error ? error.message : String(error || 'unknown')
    });
    return null;
  }
  const result = response?.result || null;
  logChatCrypto('direct', 'fetch-result', {
    directChatId: getDirectChatId(params.localIdentityId, params.peerIdentityId),
    requestedEpoch: epoch ?? 'latest',
    fetchedEpoch: result?.epoch ?? null,
    hasEnvelope: result?.hasEnvelope ?? false,
    hasEpochKey: result?.hasEpochKey ?? false,
    canRecoverByRekey: result?.canRecoverByRekey ?? null,
    keyCommitment: toCommitmentHint(result?.keyCommitment)
  });
  return result;
}

async function getValidatedCachedDirectEpochKey(
  directChatId: string,
  epoch: number,
  expectedCommitment: string | null
): Promise<Uint8Array | null> {
  const cached = await getDirectChatKey(directChatId, epoch);
  if (!cached) {
    logChatCrypto('direct', 'cache-miss', { directChatId, epoch });
    return null;
  }

  const rawKey = base64ToBytes(cached);
  if (!expectedCommitment) {
    logChatCrypto('direct', 'cache-hit-no-commitment', { directChatId, epoch });
    return rawKey;
  }

  const commitment = await computeDirectKeyCommitment(rawKey);
  if (commitment === expectedCommitment) {
    logChatCrypto('direct', 'cache-hit-valid', {
      directChatId,
      epoch,
      keyCommitment: toCommitmentHint(commitment)
    });
    return rawKey;
  }

  await deleteDirectChatKey(directChatId, epoch);
  logChatCrypto('direct', 'cache-invalidated', {
    directChatId,
    epoch,
    cachedCommitment: toCommitmentHint(commitment),
    expectedCommitment: toCommitmentHint(expectedCommitment)
  });
  return null;
}

async function tryResolveFetchedEpoch(
  params: DirectEpochContext,
  directChatId: string,
  directGuestPeer: boolean,
  fetched: DirectEpochFetchResult | null
): Promise<DirectEpochResolution | null> {
  if (!fetched?.envelopeCiphertext || !fetched.keyCommitment || !fetched.signedEpochTransition) {
    logChatCrypto('direct', 'fetched-epoch-incomplete', {
      directChatId,
      epoch: fetched?.epoch ?? null,
      hasEnvelopeCiphertext: Boolean(fetched?.envelopeCiphertext),
      hasKeyCommitment: Boolean(fetched?.keyCommitment),
      hasSignedTransition: Boolean(fetched?.signedEpochTransition)
    });
    return null;
  }

  const valid = await verifyDirectEpochTransition({
    claim: fetched.signedEpochTransition,
    directChatId,
    participantIdentityIds: getDirectChatParticipants(params.localIdentityId, params.peerIdentityId),
    epoch: fetched.epoch,
    previousEpoch: fetched.epoch > 1 ? fetched.epoch - 1 : null,
    keyCommitment: fetched.keyCommitment,
    identityPublicKeys: fetched.identityPublicKeys,
    devicesByIdentityId: fetched.devicesByIdentityId,
    allowUnattestedProposer: directGuestPeer
  });
  logChatCrypto('direct', 'transition-validated', {
    directChatId,
    epoch: fetched.epoch,
    proposerIdentityId: fetched.proposerIdentityId ?? null,
    proposerDeviceId: fetched.proposerDeviceId ?? null,
    valid
  });
  if (!valid) return null;

  const publisherKey = fetched.identityPublicKeys[fetched.publisherIdentityId || ''];
  if (!publisherKey) {
    logChatCrypto('direct', 'publisher-key-missing', {
      directChatId,
      epoch: fetched.epoch,
      publisherIdentityId: fetched.publisherIdentityId ?? null
    });
    return null;
  }

  const rawKey = await decryptDirectKeyEnvelope({
    envelopeCiphertext: fetched.envelopeCiphertext,
    publisherPublicKey: publisherKey,
    recipientEcdhKey: params.localEcdhKey,
    recipientEcdhScalar: params.localEcdhScalar
  });
  logChatCrypto('direct', 'envelope-decrypt', {
    directChatId,
    epoch: fetched.epoch,
    ok: Boolean(rawKey),
    publisherIdentityId: fetched.publisherIdentityId ?? null
  });
  if (!rawKey) return null;

  const commitment = await computeDirectKeyCommitment(rawKey);
  const commitmentMatches = commitment === fetched.keyCommitment;
  logChatCrypto('direct', 'commitment-check', {
    directChatId,
    epoch: fetched.epoch,
    ok: commitmentMatches,
    computedCommitment: toCommitmentHint(commitment),
    expectedCommitment: toCommitmentHint(fetched.keyCommitment)
  });
  if (!commitmentMatches) return null;

  await saveDirectChatKey(directChatId, fetched.epoch, bytesToBase64(rawKey));
  logChatCrypto('direct', 'resolved-from-server', {
    directChatId,
    epoch: fetched.epoch
  });
  return { key: rawKey, epoch: fetched.epoch };
}

async function publishDirectEpochKey(
  params: DirectEpochContext,
  directChatId: string,
  epoch: number,
  reason: 'initial' | 'rekey'
): Promise<DirectEpochResolution | null> {
  const localDevice = getDeviceForIdentityId(params.localIdentityId);
  if (!localDevice) {
    logChatCrypto('direct', 'publish-missing-local-device', { directChatId, epoch, reason });
    return null;
  }

  const rawKey = generateDirectKey();
  const keyCommitment = await computeDirectKeyCommitment(rawKey);
  const participantIdentityIds = getDirectChatParticipants(params.localIdentityId, params.peerIdentityId);
  const signedEpochTransition = await signDirectEpochTransition({
    directChatId,
    participantIdentityIds,
    epoch,
    previousEpoch: epoch > 1 ? epoch - 1 : null,
    keyCommitment,
    proposerIdentityId: params.localIdentityId,
    proposerDeviceId: localDevice.deviceId,
    reason,
    signingKey: localDevice.signingKey || params.localSigningKey
  });
  const envelopes = await Promise.all([
    {
      identityId: params.localIdentityId,
      envelopeCiphertext: await encryptDirectKeyEnvelope({
        rawKey,
        publisherIdentityId: params.localIdentityId,
        publisherEcdhKey: params.localEcdhKey,
        publisherEcdhScalar: params.localEcdhScalar,
        recipientPublicKey: params.localPublicKey
      })
    },
    {
      identityId: params.peerIdentityId,
      envelopeCiphertext: await encryptDirectKeyEnvelope({
        rawKey,
        publisherIdentityId: params.localIdentityId,
        publisherEcdhKey: params.localEcdhKey,
        publisherEcdhScalar: params.localEcdhScalar,
        recipientPublicKey: params.peerPublicKey
      })
    }
  ]);

  await directMessagesApi.claimOrPublishEpochKey(
    { peerIdentityId: params.peerIdentityId, epoch, keyCommitment, signedEpochTransition, envelopes },
    params.localServerUrl,
    params.localIdentityId
  );
  await saveDirectChatKey(directChatId, epoch, bytesToBase64(rawKey));
  void publishTempDeviceDirectEnvelopes({
    directChatId,
    epoch,
    rawKey,
    localIdentity: {
      identityId: params.localIdentityId,
      serverUrl: params.localServerUrl,
      publicKey: params.localPublicKey,
      ecdhKey: params.localEcdhKey,
      ecdhScalar: params.localEcdhScalar
    },
    localDevice
  });
  logChatCrypto('direct', 'published-epoch-key', {
    directChatId,
    epoch,
    reason,
    keyCommitment: toCommitmentHint(keyCommitment)
  });
  return { key: rawKey, epoch };
}

async function resolveLatestDirectEpochKey(params: DirectEpochContext): Promise<DirectEpochResolution | null> {
  const directChatId = getDirectChatId(params.localIdentityId, params.peerIdentityId);
  const existing = latestDirectEpochRecoveryInFlight.get(directChatId);
  if (existing) return existing;

  const promise = (async (): Promise<DirectEpochResolution | null> => {
    let lastResolutionError: unknown = null;
    const rememberResolutionError = (error: unknown) => {
      lastResolutionError = error;
    };
    const contact = useVaultStore.getState().findContactByIdentityId(params.peerIdentityId);
    const directGuestPeer = Boolean(contact && isDirectGuestContact(contact));
    const current = await fetchDirectEpochState(params, undefined, rememberResolutionError);
    const currentEpoch = current?.epoch || 1;
    logChatCrypto('direct', 'resolve-latest-start', {
      directChatId,
      currentEpoch,
      directGuestPeer
    });

    const cached = await getValidatedCachedDirectEpochKey(directChatId, currentEpoch, current?.keyCommitment || null);
    if (cached) {
      logChatCrypto('direct', 'resolved-from-cache', { directChatId, epoch: currentEpoch });
      return { key: cached, epoch: currentEpoch };
    }

    const fromServer = await tryResolveFetchedEpoch(params, directChatId, directGuestPeer, current);
    if (fromServer) return fromServer;

    try {
      const claimedCurrent = await publishDirectEpochKey(
        params,
        directChatId,
        currentEpoch,
        currentEpoch === 1 ? 'initial' : 'rekey'
      );
      if (claimedCurrent) return claimedCurrent;
    } catch (error) {
      rememberResolutionError(error);
      logChatCrypto('direct', 'publish-current-failed', {
        directChatId,
        epoch: currentEpoch,
        error: error instanceof Error ? error.message : String(error || 'unknown')
      });
      // Continue to refetch/recover below.
    }

    const refetchedCurrent = await fetchDirectEpochState(params, currentEpoch, rememberResolutionError);
    const resolvedCurrent = await tryResolveFetchedEpoch(params, directChatId, directGuestPeer, refetchedCurrent);
    if (resolvedCurrent) return resolvedCurrent;

    if (current?.canRecoverByRekey === false) {
      logChatCrypto('direct', 'rekey-disallowed', { directChatId, epoch: currentEpoch });
      if (lastResolutionError) throw lastResolutionError;
      return null;
    }

    const nextEpoch = Math.max(currentEpoch + 1, 2);
    try {
      const claimedNext = await publishDirectEpochKey(params, directChatId, nextEpoch, 'rekey');
      if (claimedNext) return claimedNext;
    } catch (error) {
      rememberResolutionError(error);
      logChatCrypto('direct', 'publish-next-failed', {
        directChatId,
        epoch: nextEpoch,
        error: error instanceof Error ? error.message : String(error || 'unknown')
      });
      // If another client won the repair race, the fetch below should still recover.
    }

    const refetchedNext = await fetchDirectEpochState(params, nextEpoch, rememberResolutionError);
    const resolvedNext = await tryResolveFetchedEpoch(params, directChatId, directGuestPeer, refetchedNext);
    logChatCrypto('direct', 'resolve-latest-finished', {
      directChatId,
      currentEpoch,
      nextEpoch,
      resolved: Boolean(resolvedNext),
      resolvedEpoch: resolvedNext?.epoch ?? null
    });
    if (!resolvedNext && lastResolutionError) throw lastResolutionError;
    return resolvedNext;
  })().finally(() => {
    latestDirectEpochRecoveryInFlight.delete(directChatId);
  });

  latestDirectEpochRecoveryInFlight.set(directChatId, promise);
  return promise;
}

export async function resolveDirectEpochKey(params: DirectEpochContext & { epoch?: number }): Promise<{ key: Uint8Array; epoch: number } | null> {
  const directChatId = getDirectChatId(params.localIdentityId, params.peerIdentityId);
  if (params.epoch === undefined) {
    return resolveLatestDirectEpochKey(params);
  }

  const epoch = params.epoch;
  const fetched = await fetchDirectEpochState(params, epoch);
  const cached = await getValidatedCachedDirectEpochKey(directChatId, epoch, fetched?.keyCommitment || null);
  if (cached) {
    logChatCrypto('direct', 'resolved-specific-from-cache', { directChatId, epoch });
    return { key: cached, epoch };
  }
  const contact = useVaultStore.getState().findContactByIdentityId(params.peerIdentityId);
  const directGuestPeer = Boolean(contact && isDirectGuestContact(contact));
  const resolved = await tryResolveFetchedEpoch(params, directChatId, directGuestPeer, fetched);
  logChatCrypto('direct', 'resolved-specific-finished', {
    directChatId,
    epoch,
    resolved: Boolean(resolved)
  });
  return resolved;
}

export async function ensureDirectEpochKey(params: DirectEpochContext & { epoch?: number }): Promise<Uint8Array | null> {
  const resolved = await resolveDirectEpochKey(params);
  return resolved?.key || null;
}
